/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */

import { findClosedPolygons, polygonCentroid, roomAxisAlignedBoundsM } from './floorPlanDesignerGeometry.js';
import { normalizeMaterialConfig, DEFAULT_FLOOR_TEXTURE_MATERIAL } from './floorPlanMaterialConfig.js';
import {
  MIN_WALL_LENGTH_METERS,
  addPoint,
  cleanupFloorPlanState,
  getPointById,
  isWallSegmentLongEnough,
  movePoint,
  removeWall,
} from './floorPlanDesignerState.js';
import { addWallResolvingCrossings } from './floorPlanDesignerWallCrossings.js';

export const DEFAULT_ROOM_FLOOR_COLOR = '#FFFFFF';

/**
 * @param {string[]} wallIds
 * @returns {string}
 */
export function getRoomId(wallIds) {
  return `room_${[...wallIds].sort().join('_')}`;
}

/**
 * Stable room id from boundary corner point IDs (unchanged while dragging a closed room).
 * @param {string[]} pointIds
 * @returns {string}
 */
export function getRoomTopologyId(pointIds) {
  return `room_${[...pointIds].sort().join('_')}`;
}

/**
 * @param {{ pointIds?: string[]; wallIds: string[] }} room
 * @returns {string}
 */
export function getRoomIdForDetectedRoom(room) {
  if (room.pointIds?.length) {
    return getRoomTopologyId(room.pointIds);
  }
  return getRoomId(room.wallIds);
}

/**
 * @param {string[]} wallIdsA
 * @param {string[]} wallIdsB
 * @returns {number}
 */
function countSharedWalls(wallIdsA, wallIdsB) {
  const setB = new Set(wallIdsB);
  return wallIdsA.filter((id) => setB.has(id)).length;
}

/**
 * @param {string[]} pointIdsA
 * @param {string[]} pointIdsB
 * @returns {number}
 */
function countSharedPointIds(pointIdsA, pointIdsB) {
  const setB = new Set(pointIdsB);
  return pointIdsA.filter((id) => setB.has(id)).length;
}

/**
 * @param {string} roomId
 * @returns {string[]}
 */
function tokensFromRoomId(roomId) {
  if (!roomId.startsWith('room_')) return [];
  return roomId.slice(5).split('_').filter(Boolean);
}

/**
 * @param {DesignerState} state
 * @param {Array<{ wallIds: string[] }>} [closedRooms]
 * @returns {DesignerState}
 */
export function syncRoomsMeta(state, closedRooms) {
  const rooms = closedRooms ?? findClosedPolygons(state);
  const prevMeta = state.roomsMeta ?? {};
  const nextMeta = {};
  const usedNames = new Set();
  /** @type {Set<string>} */
  const claimedPrevRoomIds = new Set();

  for (const existing of Object.values(prevMeta)) {
    if (existing?.name) usedNames.add(existing.name);
  }

  let nextRoomNumber = 1;
  const nextDefaultName = () => {
    while (usedNames.has(`Room-${nextRoomNumber}`)) {
      nextRoomNumber += 1;
    }
    const name = `Room-${nextRoomNumber}`;
    usedNames.add(name);
    nextRoomNumber += 1;
    return name;
  };

  for (const room of rooms) {
    const roomId = getRoomIdForDetectedRoom(room);
    const existing = prevMeta[roomId];
    if (existing) {
      nextMeta[roomId] = {
        name: existing.name || nextDefaultName(),
        floorColor: existing.floorColor ?? DEFAULT_ROOM_FLOOR_COLOR,
        ...(existing.floorMaterial ? { floorMaterial: existing.floorMaterial } : {}),
        visibleIn3D: existing.visibleIn3D ?? true,
      };
      if (existing.name) usedNames.add(existing.name);
      claimedPrevRoomIds.add(roomId);
      continue;
    }

    let inheritedMeta = null;
    let inheritedPrevId = null;
    let bestPointOverlap = 0;
    let bestWallOverlap = 0;
    for (const [prevRoomId, meta] of Object.entries(prevMeta)) {
      if (claimedPrevRoomIds.has(prevRoomId)) continue;
      const prevTokensList = tokensFromRoomId(prevRoomId);
      const pointOverlap = room.pointIds?.length
        ? countSharedPointIds(room.pointIds, prevTokensList)
        : 0;
      const wallOverlap = countSharedWalls(room.wallIds, prevTokensList);
      const score = pointOverlap * 10 + wallOverlap;
      const bestScore = bestPointOverlap * 10 + bestWallOverlap;
      if (score > bestScore) {
        bestPointOverlap = pointOverlap;
        bestWallOverlap = wallOverlap;
        inheritedMeta = meta;
        inheritedPrevId = prevRoomId;
      }
    }

    const inheritsByPoints = bestPointOverlap >= 3;
    const inheritsByWalls = bestWallOverlap >= 2;
    if (inheritedMeta && inheritedPrevId && (inheritsByPoints || inheritsByWalls)) {
      claimedPrevRoomIds.add(inheritedPrevId);
      nextMeta[roomId] = {
        name: inheritedMeta.name || nextDefaultName(),
        floorColor: inheritedMeta.floorColor ?? DEFAULT_ROOM_FLOOR_COLOR,
        ...(inheritedMeta.floorMaterial ? { floorMaterial: inheritedMeta.floorMaterial } : {}),
        visibleIn3D: inheritedMeta.visibleIn3D ?? true,
      };
      if (inheritedMeta.name) usedNames.add(inheritedMeta.name);
    } else {
      nextMeta[roomId] = {
        name: nextDefaultName(),
        floorColor: DEFAULT_ROOM_FLOOR_COLOR,
        floorMaterial: { ...DEFAULT_FLOOR_TEXTURE_MATERIAL },
        visibleIn3D: true,
      };
    }
  }

  return { ...state, roomsMeta: nextMeta };
}

/**
 * @param {DesignerState} state
 * @param {string} roomId
 * @param {string} name
 * @returns {DesignerState}
 */
export function setRoomName(state, roomId, name) {
  const trimmed = String(name).trim();
  if (!trimmed) return state;
  const prev = state.roomsMeta?.[roomId] ?? { name: 'Room', floorColor: DEFAULT_ROOM_FLOOR_COLOR };
  return {
    ...state,
    roomsMeta: {
      ...(state.roomsMeta ?? {}),
      [roomId]: { ...prev, name: trimmed },
    },
  };
}

/**
 * @param {DesignerState} state
 * @param {string} roomId
 * @param {string} color
 * @returns {DesignerState}
 */
export function setRoomFloorColor(state, roomId, color) {
  const hex = normalizeMaterialConfig({ materialMode: 'color', color, textureId: null }).color;
  const prev = state.roomsMeta?.[roomId] ?? { name: 'Room', floorColor: DEFAULT_ROOM_FLOOR_COLOR };
  return {
    ...state,
    roomsMeta: {
      ...(state.roomsMeta ?? {}),
      [roomId]: {
        ...prev,
        floorColor: hex,
        floorMaterial: { materialMode: 'color', color: hex, textureId: null },
      },
    },
  };
}

/**
 * @param {DesignerState} state
 * @param {string} roomId
 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig} material
 * @returns {DesignerState}
 */
export function setRoomFloorMaterial(state, roomId, material) {
  const normalized = normalizeMaterialConfig(material);
  const prev = state.roomsMeta?.[roomId] ?? { name: 'Room', floorColor: DEFAULT_ROOM_FLOOR_COLOR };
  return {
    ...state,
    roomsMeta: {
      ...(state.roomsMeta ?? {}),
      [roomId]: { ...prev, floorMaterial: normalized },
    },
  };
}

/**
 * @param {DesignerState} state
 * @param {string} roomId
 * @param {boolean} visible
 * @returns {DesignerState}
 */
export function setRoomVisibleIn3D(state, roomId, visible) {
  const prev = state.roomsMeta?.[roomId] ?? {
    name: 'Room',
    floorColor: DEFAULT_ROOM_FLOOR_COLOR,
    visibleIn3D: true,
  };
  return {
    ...state,
    roomsMeta: {
      ...(state.roomsMeta ?? {}),
      [roomId]: { ...prev, visibleIn3D: visible !== false },
    },
  };
}

/**
 * @param {DesignerState} state
 * @param {string[]} wallIds
 * @returns {DesignerState}
 */
export function deleteRoom(state, wallIds) {
  let next = state;
  for (const wallId of wallIds) {
    next = removeWall(next, wallId);
  }
  return syncRoomsMeta(cleanupFloorPlanState(next));
}

/**
 * @param {DesignerState} state
 * @param {string[]} pointIds
 * @param {Point} polygon
 * @param {number} targetWidthM
 * @param {number} targetDepthM
 * @returns {{ state: DesignerState; ok: boolean; error?: string }}
 */
export function resizeRoomAxisAligned(state, pointIds, polygon, targetWidthM, targetDepthM) {
  if (!Number.isFinite(targetWidthM) || !Number.isFinite(targetDepthM)) {
    return { state, ok: false, error: 'Width and depth must be valid numbers.' };
  }
  if (targetWidthM < MIN_WALL_LENGTH_METERS || targetDepthM < MIN_WALL_LENGTH_METERS) {
    return {
      state,
      ok: false,
      error: `Dimensions must be at least ${MIN_WALL_LENGTH_METERS} m.`,
    };
  }

  const mpp = state.scale.metersPerPixel;
  const { widthM, depthM } = roomAxisAlignedBoundsM(polygon, mpp);
  if (widthM < 1e-6 || depthM < 1e-6) {
    return { state, ok: false, error: 'Room has invalid current dimensions.' };
  }

  const scaleX = targetWidthM / widthM;
  const scaleY = targetDepthM / depthM;
  const [cx, cy] = polygonCentroid(polygon);
  const uniquePointIds = [...new Set(pointIds)];

  let next = state;
  for (const pointId of uniquePointIds) {
    const pt = getPointById(next, pointId);
    if (!pt) continue;
    const newCoords = [cx + (pt.x - cx) * scaleX, cy + (pt.y - cy) * scaleY];
    next = movePoint(next, pointId, newCoords);
  }

  next = cleanupFloorPlanState(next);
  for (const wall of next.walls) {
    if (
      !isWallSegmentLongEnough(next, wall.startPointId, wall.endPointId)
    ) {
      return {
        state,
        ok: false,
        error: `Resize would create a wall shorter than ${MIN_WALL_LENGTH_METERS} m.`,
      };
    }
  }

  return { state: syncRoomsMeta(next), ok: true };
}

/**
 * @param {DesignerState} state
 * @param {string} roomId
 * @returns {{ name: string; floorColor: string; floorMaterial?: import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig; visibleIn3D: boolean } | null}
 */
export function getRoomMeta(state, roomId) {
  const meta = state.roomsMeta?.[roomId];
  if (!meta) return null;
  return {
    name: meta.name,
    floorColor: meta.floorColor ?? DEFAULT_ROOM_FLOOR_COLOR,
    floorMaterial: meta.floorMaterial,
    visibleIn3D: meta.visibleIn3D ?? true,
  };
}

/**
 * Snap a dragged corner to form an axis-aligned rectangle with the anchor.
 * @param {Point} anchor
 * @param {Point} snappedPointer
 * @returns {Point}
 */
export function resolveRectangularRoomCorner(_anchor, snappedPointer) {
  return [snappedPointer[0], snappedPointer[1]];
}

/**
 * @param {DesignerState} state
 * @param {Point} cornerA
 * @param {Point} cornerB
 * @returns {boolean}
 */
export function isRectangularRoomLargeEnough(state, cornerA, cornerB) {
  const minX = Math.min(cornerA[0], cornerB[0]);
  const maxX = Math.max(cornerA[0], cornerB[0]);
  const minY = Math.min(cornerA[1], cornerB[1]);
  const maxY = Math.max(cornerA[1], cornerB[1]);
  const minLenPx = MIN_WALL_LENGTH_METERS * state.scale.pixelsPerMeter;
  return maxX - minX >= minLenPx && maxY - minY >= minLenPx;
}

/**
 * Create a rectangular room from two opposite corners using existing wall mutations.
 * @param {DesignerState} state
 * @param {Point} cornerA
 * @param {Point} cornerB
 * @returns {DesignerState}
 */
export function addRectangularRoom(state, cornerA, cornerB) {
  if (!isRectangularRoomLargeEnough(state, cornerA, cornerB)) {
    return state;
  }

  const minX = Math.min(cornerA[0], cornerB[0]);
  const maxX = Math.max(cornerA[0], cornerB[0]);
  const minY = Math.min(cornerA[1], cornerB[1]);
  const maxY = Math.max(cornerA[1], cornerB[1]);

  const corners = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];

  let next = state;
  const pointIds = [];
  for (const coords of corners) {
    const added = addPoint(next, coords);
    next = added.state;
    pointIds.push(added.pointId);
  }

  for (let index = 0; index < 4; index += 1) {
    const startId = pointIds[index];
    const endId = pointIds[(index + 1) % 4];
    if (isWallSegmentLongEnough(next, startId, endId)) {
      next = addWallResolvingCrossings(next, startId, endId);
    }
  }

  return cleanupFloorPlanState(next);
}
